import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-zinc-200' 
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      {/* Top Bar */}
      <div className="bg-black border-b border-zinc-800 py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4 text-zinc-300">
            <a href="tel:9566030388" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">+91 9566030388</span>
            </a>
            <a href="mailto:rk.rotoarc1@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">rk.rotoarc1@gmail.com</span>
            </a>
          </div>
          <div className="text-zinc-300 text-xs hidden md:block">
            Trusted Since 2015 | Chennai, Tamil Nadu
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_rk-rotoarc-preview/artifacts/qs3pkdc4_IMG_4982.jpeg" 
              alt="RK ROTOARC" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('products')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              Products
            </button>
            <button 
              onClick={() => scrollToSection('industries')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              Industries
            </button>
            <button 
              onClick={() => scrollToSection('dealer')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              Become a Dealer
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-zinc-700 hover:text-yellow-500 font-semibold transition-colors"
            >
              Contact
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => scrollToSection('products')}
              className="border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-500 hover:text-black font-semibold transition-colors"
            >
              View Products
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-zinc-700 hover:text-yellow-500 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-zinc-200">
            <nav className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('home')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('products')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                Products
              </button>
              <button 
                onClick={() => scrollToSection('industries')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                Industries
              </button>
              <button 
                onClick={() => scrollToSection('dealer')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                Become a Dealer
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-zinc-700 hover:text-yellow-500 font-semibold text-left transition-colors"
              >
                Contact
              </button>
              <Button 
                onClick={() => scrollToSection('products')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold mt-2"
              >
                View Products
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;